import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from 'recharts';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Skeleton } from '../../components/ui/skeleton';
import { Alert, AlertDescription, AlertTitle } from '../../components/ui/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { toast } from 'sonner';
import { ShieldAlert, Users, TrendingUp, Mail, BarChart3, RefreshCw, AlertTriangle, Eye, MousePointerClick, FlaskConical, Trophy, Plus, Pause, Play, CheckCircle2, Trash2, Sigma } from 'lucide-react';

const LANG_FLAG = { it: '🇮🇹', en: '🇬🇧', fr: '🇫🇷', de: '🇩🇪', es: '🇪🇸' };
const LANG_NAME = { it: 'Italian', en: 'English', fr: 'Français', de: 'Deutsch', es: 'Español' };
const PERIOD_OPTIONS = [
    { value: 'all', days: null, labelKey: 'admin.period_all' },
    { value: '7', days: 7, labelKey: 'admin.period_7' },
    { value: '30', days: 30, labelKey: 'admin.period_30' },
    { value: '90', days: 90, labelKey: 'admin.period_90' },
];

// Aligned with design_guidelines.md chart palette
const CHART_PALETTE = [
    'hsl(45, 100%, 52%)',  // chart-2 (accento giallo)
    'hsl(210, 90%, 40%)',  // chart-3 (info blue)
    'hsl(145, 55%, 34%)',  // chart-4 (success green)
    'hsl(0, 72%, 52%)',    // chart-5 (destructive red)
    'hsl(220, 13%, 10%)',  // chart-1 (ink dark)
];

const tooltipStyle = {
    background: 'hsl(220, 13%, 10%)',
    border: 'none',
    borderRadius: 8,
    color: '#fff',
    padding: '8px 12px',
    fontSize: 12,
};

export default function AnalyticsPage() {
    const { t } = useTranslation();
    const { user } = useAuth();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState(null);
    const [refreshing, setRefreshing] = useState(false);
    const [period, setPeriod] = useState('all');

    const isAdmin = user && (user.role === 'admin' || ['demo@cantiere.ai', 'alessandroscarpuzza@gmail.com'].includes((user.email || '').toLowerCase()));

    const fetchData = useCallback(async () => {
        try {
            const opt = PERIOD_OPTIONS.find(p => p.value === period);
            const q = opt && opt.days ? `?days=${opt.days}` : '';
            const res = await api.get(`/auth/admin/language-analytics${q}`);
            setData(res.data);
            setErr(null);
        } catch (e) {
            setErr(e.response?.data?.detail || e.message);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, [period]);

    useEffect(() => {
        if (!isAdmin) { setLoading(false); return; }
        setRefreshing(true);
        fetchData();
    }, [isAdmin, fetchData]);

    const refresh = () => { setRefreshing(true); fetchData(); };

    if (!isAdmin) {
        return (
            <div className="p-10 text-center text-muted-foreground" data-testid="analytics-admin-only">
                <ShieldAlert className="w-12 h-12 mx-auto mb-3" />
                <p className="text-lg font-semibold">{t('admin.admin_only')}</p>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="space-y-5" data-testid="analytics-loading">
                <Skeleton className="h-12 w-72" />
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">{[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-28" />)}</div>
                <div className="grid lg:grid-cols-2 gap-5">{[1, 2].map(i => <Skeleton key={i} className="h-80" />)}</div>
            </div>
        );
    }

    if (err) {
        return (
            <div data-testid="analytics-error">
                <Alert variant="destructive" className="max-w-2xl">
                    <AlertTriangle className="w-4 h-4" />
                    <AlertTitle>{t('admin.error_loading')}</AlertTitle>
                    <AlertDescription className="mt-1 text-sm">{err}</AlertDescription>
                </Alert>
                <Button onClick={refresh} className="mt-3 gap-2" variant="outline" data-testid="analytics-retry">
                    <RefreshCw className="w-4 h-4" /> {t('admin.retry')}
                </Button>
            </div>
        );
    }

    const report = data?.report_by_language || [];
    const totals = data?.totals || {};

    const totalSignups = report.reduce((acc, r) => acc + (r.signups || 0), 0);
    const totalPaid = report.reduce((acc, r) => acc + (r.paid || 0), 0);
    const totalActive = report.reduce((acc, r) => acc + (r.current_active_users || 0), 0);
    const totalEmailsSent = report.reduce((acc, r) => acc + (r.emails_sent || 0), 0);
    const avgConv = totalSignups > 0 ? ((totalPaid / totalSignups) * 100).toFixed(2) : '0.00';

    const labelFor = (lang) => LANG_NAME[lang] || (lang ? lang.toUpperCase() : 'N/A');

    const chartData = report.map((r) => ({
        lang: r.language?.toUpperCase() || 'N/A',
        langFull: labelFor(r.language),
        signups: r.signups || 0,
        paid: r.paid || 0,
        active: r.current_active_users || 0,
        conv: r.conversion_rate_pct || 0,
        ctr: r.email_ctr_pct || 0,
        opens: r.emails_opened || 0,
        clicks: r.emails_clicked || 0,
    }));

    return (
        <div data-testid="analytics-page">
            {/* Header */}
            <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
                <div>
                    <div className="text-xs tracking-widest text-muted-foreground mb-1">{t('admin.label_admin')}</div>
                    <h1 className="font-display text-4xl md:text-5xl flex items-center gap-3">
                        <BarChart3 className="w-10 h-10 text-[hsl(var(--accent))]" /> {t('admin.analytics_title')}
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1">{t('admin.analytics_subtitle')}</p>
                    {data?.period?.since && (
                        <p className="text-xs text-muted-foreground mt-1">
                            {t('admin.period_since')} {new Date(data.period.since).toLocaleDateString()}
                        </p>
                    )}
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <div className="flex flex-col">
                        <Label className="text-xs text-muted-foreground mb-1">{t('admin.period_label')}</Label>
                        <Select value={period} onValueChange={setPeriod}>
                            <SelectTrigger className="w-44 h-10" data-testid="analytics-period">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {PERIOD_OPTIONS.map(p => (
                                    <SelectItem key={p.value} value={p.value} data-testid={`period-opt-${p.value}`}>
                                        {t(p.labelKey)}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <Button
                        onClick={refresh}
                        variant="outline"
                        className="gap-2 h-10 mt-5"
                        disabled={refreshing}
                        data-testid="analytics-refresh"
                    >
                        <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} /> {t('admin.refresh')}
                    </Button>
                </div>
            </div>

            {/* KPI cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <Card className="p-5">
                    <div className="flex items-center justify-between mb-2">
                        <div className="text-xs text-muted-foreground uppercase tracking-wide">{t('admin.kpi_signups')}</div>
                        <Users className="w-5 h-5 text-[hsl(var(--accent))]" />
                    </div>
                    <div className="font-display text-4xl tabular-nums" data-testid="kpi-total-signups">{totalSignups}</div>
                </Card>
                <Card className="p-5">
                    <div className="flex items-center justify-between mb-2">
                        <div className="text-xs text-muted-foreground uppercase tracking-wide">{t('admin.kpi_paid')}</div>
                        <TrendingUp className="w-5 h-5 text-[hsl(145,55%,34%)]" />
                    </div>
                    <div className="font-display text-4xl tabular-nums" data-testid="kpi-total-paid">{totalPaid}</div>
                </Card>
                <Card className="p-5">
                    <div className="flex items-center justify-between mb-2">
                        <div className="text-xs text-muted-foreground uppercase tracking-wide">{t('admin.kpi_conv')}</div>
                        <TrendingUp className="w-5 h-5 text-[hsl(var(--accent))]" />
                    </div>
                    <div className="font-display text-4xl tabular-nums" data-testid="kpi-avg-conv">{avgConv}%</div>
                </Card>
                <Card className="p-5">
                    <div className="flex items-center justify-between mb-2">
                        <div className="text-xs text-muted-foreground uppercase tracking-wide">{t('admin.kpi_active')}</div>
                        <Users className="w-5 h-5 text-[hsl(210,90%,40%)]" />
                    </div>
                    <div className="font-display text-4xl tabular-nums" data-testid="kpi-total-active">{totalActive}</div>
                </Card>
            </div>

            {/* Charts row 1 */}
            <div className="grid lg:grid-cols-2 gap-5 mb-6">
                {/* Signups bar chart */}
                <Card className="p-5">
                    <h2 className="font-display text-2xl mb-4">{t('admin.chart_signups')}</h2>
                    {chartData.length === 0 ? (
                        <div className="text-center py-12 text-muted-foreground">{t('admin.no_data')}</div>
                    ) : (
                        <ResponsiveContainer width="100%" height={300}>
                            <BarChart data={chartData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                                <XAxis dataKey="lang" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} allowDecimals={false} />
                                <Tooltip contentStyle={tooltipStyle} cursor={{ fill: 'hsl(45, 100%, 52%, 0.08)' }} />
                                <Legend wrapperStyle={{ fontSize: 12 }} />
                                <Bar dataKey="signups" fill={CHART_PALETTE[0]} name={t('admin.kpi_signups')} radius={[4, 4, 0, 0]} />
                                <Bar dataKey="paid" fill={CHART_PALETTE[2]} name={t('admin.kpi_paid')} radius={[4, 4, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    )}
                </Card>

                {/* Conversion rate */}
                <Card className="p-5">
                    <h2 className="font-display text-2xl mb-4">{t('admin.chart_conv')}</h2>
                    {chartData.length === 0 ? (
                        <div className="text-center py-12 text-muted-foreground">{t('admin.no_data')}</div>
                    ) : (
                        <ResponsiveContainer width="100%" height={300}>
                            <BarChart data={chartData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                                <XAxis dataKey="lang" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} unit="%" />
                                <Tooltip
                                    contentStyle={tooltipStyle}
                                    cursor={{ fill: 'hsl(45, 100%, 52%, 0.08)' }}
                                    formatter={(v) => [`${v}%`, t('admin.kpi_conv')]}
                                />
                                <Bar dataKey="conv" name={t('admin.kpi_conv')} radius={[4, 4, 0, 0]}>
                                    {chartData.map((entry, idx) => <Cell key={idx} fill={CHART_PALETTE[idx % CHART_PALETTE.length]} />)}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    )}
                </Card>
            </div>

            {/* Email engagement chart */}
            <Card className="p-5 mb-6">
                <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                    <h2 className="font-display text-2xl flex items-center gap-2">
                        <Mail className="w-6 h-6 text-[hsl(var(--accent))]" /> {t('admin.chart_emails_title')}
                    </h2>
                    <div className="flex gap-4 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1"><Eye className="w-3 h-3" /> {t('admin.opens')}</span>
                        <span className="flex items-center gap-1"><MousePointerClick className="w-3 h-3" /> {t('admin.clicks')}</span>
                    </div>
                </div>
                {totalEmailsSent === 0 ? (
                    <div className="text-center py-10 text-muted-foreground">{t('admin.no_data')}</div>
                ) : (
                    <ResponsiveContainer width="100%" height={260}>
                        <BarChart data={chartData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                            <XAxis dataKey="lang" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                            <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} allowDecimals={false} />
                            <Tooltip contentStyle={tooltipStyle} cursor={{ fill: 'hsl(45, 100%, 52%, 0.08)' }} />
                            <Legend wrapperStyle={{ fontSize: 12 }} />
                            <Bar dataKey="opens" name={t('admin.opens')} fill={CHART_PALETTE[1]} radius={[4, 4, 0, 0]} />
                            <Bar dataKey="clicks" name={t('admin.clicks')} fill={CHART_PALETTE[0]} radius={[4, 4, 0, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                )}
            </Card>

            {/* CTR chart (horizontal) */}
            <Card className="p-5 mb-6">
                <h2 className="font-display text-2xl mb-4">{t('admin.chart_ctr')}</h2>
                {chartData.length === 0 || chartData.every(c => !c.ctr) ? (
                    <div className="text-center py-10 text-muted-foreground">{t('admin.no_data')}</div>
                ) : (
                    <ResponsiveContainer width="100%" height={Math.max(180, chartData.length * 48)}>
                        <BarChart data={chartData} layout="vertical" margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                            <XAxis type="number" stroke="hsl(var(--muted-foreground))" fontSize={12} unit="%" />
                            <YAxis type="category" dataKey="lang" stroke="hsl(var(--muted-foreground))" fontSize={12} width={60} />
                            <Tooltip
                                contentStyle={tooltipStyle}
                                cursor={{ fill: 'hsl(45, 100%, 52%, 0.08)' }}
                                formatter={(v) => [`${v}%`, 'CTR']}
                            />
                            <Bar dataKey="ctr" name="CTR" radius={[0, 4, 4, 0]}>
                                {chartData.map((entry, idx) => <Cell key={idx} fill={CHART_PALETTE[idx % CHART_PALETTE.length]} />)}
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                )}
            </Card>

            {/* Detailed table */}
            <Card className="overflow-hidden">
                <div className="p-5">
                    <h2 className="font-display text-2xl mb-4">{t('admin.detail_title')}</h2>
                    <div className="overflow-x-auto">
                        <Table data-testid="analytics-table">
                            <TableHeader>
                                <TableRow>
                                    <TableHead>{t('admin.table_lang')}</TableHead>
                                    <TableHead className="text-right">{t('admin.table_signups')}</TableHead>
                                    <TableHead className="text-right">{t('admin.table_paid')}</TableHead>
                                    <TableHead className="text-right">{t('admin.table_conv')}</TableHead>
                                    <TableHead className="text-right">{t('admin.table_active')}</TableHead>
                                    <TableHead className="text-right hidden md:table-cell">{t('admin.opens')}</TableHead>
                                    <TableHead className="text-right hidden md:table-cell">{t('admin.clicks')}</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {report.length === 0 ? (
                                    <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-6">{t('admin.no_data')}</TableCell></TableRow>
                                ) : report.map((row) => (
                                    <TableRow key={row.language} data-testid={`analytics-row-${row.language}`}>
                                        <TableCell className="font-medium">
                                            <span className="mr-2">{LANG_FLAG[row.language] || '🌐'}</span>
                                            {labelFor(row.language)}
                                        </TableCell>
                                        <TableCell className="text-right tabular-nums">{row.signups}</TableCell>
                                        <TableCell className="text-right tabular-nums">{row.paid}</TableCell>
                                        <TableCell className="text-right tabular-nums">
                                            {row.conversion_rate_pct > 0
                                                ? <Badge className="bg-[hsl(145,55%,90%)] text-[hsl(145,55%,30%)] hover:bg-[hsl(145,55%,90%)]">{row.conversion_rate_pct}%</Badge>
                                                : <span className="text-muted-foreground">—</span>}
                                        </TableCell>
                                        <TableCell className="text-right tabular-nums">{row.current_active_users}</TableCell>
                                        <TableCell className="text-right tabular-nums hidden md:table-cell">{row.emails_opened || 0}</TableCell>
                                        <TableCell className="text-right tabular-nums hidden md:table-cell">{row.emails_clicked || 0}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                </div>
            </Card>

            {/* Footer note */}
            <div className="mt-4 text-sm text-muted-foreground flex flex-wrap gap-x-6 gap-y-1">
                <span>
                    📊 {t('admin.lang_changes_label')}:&nbsp;
                    <strong>{totals.users_who_changed_language || 0}</strong>
                    &nbsp;{t('admin.users_label')}
                    &nbsp;({totals.lang_change_rate_pct || 0}% {t('admin.of_label')} {totals.total_users || 0})
                </span>
            </div>

            <p className="mt-2 text-xs text-muted-foreground italic">{t('admin.note_help')}</p>

            {/* A/B Testing section */}
            <AbTestsPanel />
        </div>
    );
}

// ============================================================
// A/B Testing Panel - shows active experiments + report
// ============================================================
function AbTestsPanel() {
    const { t } = useTranslation();
    const [experiments, setExperiments] = useState([]);
    const [reports, setReports] = useState({});
    const [loading, setLoading] = useState(true);
    const [showNew, setShowNew] = useState(false);

    const load = useCallback(async () => {
        try {
            const res = await api.get('/ab/experiments');
            setExperiments(res.data);
            const reps = {};
            await Promise.all(res.data.map(async (exp) => {
                try {
                    const r = await api.get(`/ab/report/${exp.key}`);
                    reps[exp.key] = r.data;
                } catch (e) { /* skip */ }
            }));
            setReports(reps);
        } catch (e) { /* */ }
        finally { setLoading(false); }
    }, []);

    useEffect(() => { load(); }, [load]);

    const updateStatus = async (key, status) => {
        try {
            await api.put(`/ab/experiments/${key}/status?status=${status}`);
            toast.success(t('ab.status_updated'));
            load();
        } catch (e) { toast.error(e.response?.data?.detail || t('common.generic_error')); }
    };

    return (
        <div className="mt-8" data-testid="ab-tests-panel">
            <div className="mb-4 flex items-start justify-between flex-wrap gap-2">
                <div>
                    <h2 className="font-display text-3xl flex items-center gap-2">
                        <FlaskConical className="w-7 h-7 text-[hsl(var(--accent))]" /> {t('ab.section_title')}
                    </h2>
                    <p className="text-sm text-muted-foreground mt-1">{t('ab.subtitle')}</p>
                </div>
                <Button onClick={() => setShowNew(true)} className="bg-[hsl(var(--accent))] text-[hsl(220_13%_10%)] gap-2 h-10 font-bold" data-testid="ab-new-button">
                    <Plus className="w-4 h-4" /> {t('ab.new_experiment')}
                </Button>
            </div>

            {loading ? (
                <Skeleton className="h-40" />
            ) : experiments.length === 0 ? (
                <Card className="p-8 text-center text-muted-foreground" data-testid="ab-empty">
                    <FlaskConical className="w-10 h-10 mx-auto mb-2 text-muted-foreground" />
                    <p className="text-sm">{t('ab.no_experiments')}</p>
                </Card>
            ) : (
                <div className="space-y-4">
                    {experiments.map((exp) => (
                        <AbExperimentCard
                            key={exp.key}
                            exp={exp}
                            report={reports[exp.key]}
                            onStatusChange={(s) => updateStatus(exp.key, s)}
                        />
                    ))}
                </div>
            )}

            <NewExperimentDialog open={showNew} onOpenChange={setShowNew} onCreated={() => { setShowNew(false); load(); }} />
        </div>
    );
}

function AbExperimentCard({ exp, report, onStatusChange }) {
    const { t } = useTranslation();
    const totals = report?.totals || {};
    const significance = report?.significance || {};
    const sampleWarnings = report?.sample_warnings || {};
    const minSampleSize = report?.min_sample_size || 100;
    const variantKeys = Object.keys(totals);

    // Find winner with at least min sample size and significant p-value
    const winner = variantKeys.reduce((best, k) => {
        const cur = totals[k];
        if (cur.conversions === 0) return best;
        if (!best) return { key: k, ...cur };
        return cur.cvr_pct > best.cvr_pct ? { key: k, ...cur } : best;
    }, null);

    const controlKey = exp.variants[0]?.key;
    const controlCvr = totals[controlKey]?.cvr_pct || 0;
    const isWinnerSignificant = winner && significance[winner.key]?.is_significant === true && winner.exposures >= minSampleSize;

    const statusLabel = exp.status === 'active'
        ? t('ab.status_active')
        : exp.status === 'paused' ? t('ab.status_paused') : exp.status;
    const statusColor = exp.status === 'active'
        ? 'bg-[hsl(145,55%,90%)] text-[hsl(145,55%,30%)]'
        : exp.status === 'paused' ? 'bg-[hsl(45,100%,90%)] text-[hsl(35,80%,25%)]'
        : 'bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))]';

    return (
        <Card className="p-5" data-testid={`ab-experiment-${exp.key}`}>
            <div className="flex items-start justify-between mb-4 flex-wrap gap-2">
                <div>
                    <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-display text-xl">{exp.name}</h3>
                        <Badge className={`${statusColor} hover:${statusColor}`}>{statusLabel}</Badge>
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                        <code className="bg-[hsl(var(--muted))] px-1 py-0.5 rounded">{exp.key}</code>
                        {' · '}
                        {exp.lang_scope?.length ? exp.lang_scope.map(l => l.toUpperCase()).join(', ') : t('ab.lang_all')}
                    </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    {isWinnerSignificant && (
                        <Badge className="bg-[hsl(45,100%,90%)] text-[hsl(35,80%,25%)] hover:bg-[hsl(45,100%,90%)] gap-1">
                            <Trophy className="w-3 h-3" /> {t('ab.winner')}: {winner.key}
                        </Badge>
                    )}
                    {exp.status === 'active' && (
                        <Button size="sm" variant="outline" className="gap-1 h-8" onClick={() => onStatusChange('paused')} data-testid={`ab-pause-${exp.key}`}>
                            <Pause className="w-3 h-3" /> {t('ab.action_pause')}
                        </Button>
                    )}
                    {exp.status === 'paused' && (
                        <Button size="sm" variant="outline" className="gap-1 h-8" onClick={() => onStatusChange('active')} data-testid={`ab-activate-${exp.key}`}>
                            <Play className="w-3 h-3" /> {t('ab.action_activate')}
                        </Button>
                    )}
                    {(exp.status === 'active' || exp.status === 'paused') && (
                        <Button size="sm" variant="outline" className="gap-1 h-8" onClick={() => onStatusChange('completed')} data-testid={`ab-complete-${exp.key}`}>
                            <CheckCircle2 className="w-3 h-3" /> {t('ab.action_complete')}
                        </Button>
                    )}
                </div>
            </div>

            <div className="overflow-x-auto">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>{t('ab.variant')}</TableHead>
                            <TableHead className="text-right">{t('ab.exposures')}</TableHead>
                            <TableHead className="text-right">{t('ab.conversions')}</TableHead>
                            <TableHead className="text-right">{t('ab.cvr')}</TableHead>
                            <TableHead className="text-right">{t('ab.lift')}</TableHead>
                            <TableHead className="text-right hidden md:table-cell">{t('ab.significance')}</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {exp.variants.map((v) => {
                            const stats = totals[v.key] || { exposures: 0, conversions: 0, cvr_pct: 0 };
                            const lift = v.key === controlKey ? 0 : stats.cvr_pct - controlCvr;
                            const sig = significance[v.key] || {};
                            const lowSample = sampleWarnings[v.key];
                            const variantIsWinner = winner && winner.key === v.key && isWinnerSignificant;
                            return (
                                <TableRow key={v.key} data-testid={`ab-variant-${exp.key}-${v.key}`}>
                                    <TableCell>
                                        <div className="font-medium flex items-center gap-1">
                                            {v.label}
                                            {lowSample && (
                                                <span title={t('ab.sample_warning', { n: minSampleSize })}>
                                                    <AlertTriangle className="w-3.5 h-3.5 text-[hsl(45,100%,40%)]" data-testid={`ab-sample-warn-${exp.key}-${v.key}`} />
                                                </span>
                                            )}
                                        </div>
                                        <div className="text-xs text-muted-foreground"><code>{v.key}</code> · {v.weight}%</div>
                                    </TableCell>
                                    <TableCell className="text-right tabular-nums">{stats.exposures}</TableCell>
                                    <TableCell className="text-right tabular-nums">{stats.conversions}</TableCell>
                                    <TableCell className="text-right tabular-nums">
                                        {variantIsWinner
                                            ? <Badge className="bg-[hsl(145,55%,90%)] text-[hsl(145,55%,30%)] hover:bg-[hsl(145,55%,90%)]">{stats.cvr_pct}%</Badge>
                                            : <span>{stats.cvr_pct}%</span>}
                                    </TableCell>
                                    <TableCell className="text-right tabular-nums">
                                        {v.key === controlKey
                                            ? <span className="text-muted-foreground">—</span>
                                            : <span className={lift > 0 ? 'text-[hsl(145,55%,34%)] font-semibold' : lift < 0 ? 'text-[hsl(0,72%,52%)]' : 'text-muted-foreground'}>
                                                {lift > 0 ? '+' : ''}{lift.toFixed(2)} pp
                                            </span>}
                                    </TableCell>
                                    <TableCell className="text-right hidden md:table-cell">
                                        {v.key === controlKey ? (
                                            <span className="text-xs text-muted-foreground">{t('ab.cvr')} base</span>
                                        ) : sig.p_value !== undefined ? (
                                            <div className="text-xs leading-tight">
                                                <div className="flex items-center justify-end gap-1">
                                                    <Sigma className="w-3 h-3 text-muted-foreground" />
                                                    <span className="font-mono">p={sig.p_value}</span>
                                                </div>
                                                {sig.is_significant ? (
                                                    <Badge className="mt-0.5 bg-[hsl(145,55%,90%)] text-[hsl(145,55%,30%)] hover:bg-[hsl(145,55%,90%)] text-[10px]">
                                                        ✓ {t('ab.significant')}
                                                    </Badge>
                                                ) : (
                                                    <span className="text-[10px] text-muted-foreground">{t('ab.not_significant')}</span>
                                                )}
                                                <div className="text-[10px] text-muted-foreground mt-0.5">
                                                    {t('ab.ci_diff')}: [{sig.ci_diff_low_pct}, {sig.ci_diff_high_pct}]
                                                </div>
                                            </div>
                                        ) : <span className="text-muted-foreground">—</span>}
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </div>

            {/* Sample size warning banner */}
            {Object.values(sampleWarnings).some(Boolean) && (
                <Alert className="mt-3 border-[hsl(45,100%,50%)] bg-[hsl(45,100%,96%)]">
                    <AlertTriangle className="w-4 h-4 text-[hsl(45,100%,40%)]" />
                    <AlertDescription className="text-xs">
                        {t('ab.sample_warning', { n: minSampleSize })}
                    </AlertDescription>
                </Alert>
            )}

            {/* Per-language breakdown */}
            {report?.rows?.length > 0 && (
                <details className="mt-3">
                    <summary className="cursor-pointer text-sm text-muted-foreground hover:text-foreground">
                        ▸ {t('admin.detail_title')}
                    </summary>
                    <Table className="mt-2">
                        <TableHeader>
                            <TableRow>
                                <TableHead>{t('ab.variant')}</TableHead>
                                <TableHead>{t('admin.table_lang')}</TableHead>
                                <TableHead className="text-right">{t('ab.exposures')}</TableHead>
                                <TableHead className="text-right">{t('ab.conversions')}</TableHead>
                                <TableHead className="text-right">{t('ab.cvr')}</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {report.rows.map((r, i) => (
                                <TableRow key={i}>
                                    <TableCell className="font-mono text-xs">{r.variant}</TableCell>
                                    <TableCell><span className="mr-1">{LANG_FLAG[r.lang] || '🌐'}</span>{r.lang.toUpperCase()}</TableCell>
                                    <TableCell className="text-right tabular-nums">{r.exposures}</TableCell>
                                    <TableCell className="text-right tabular-nums">{r.conversions}</TableCell>
                                    <TableCell className="text-right tabular-nums">{r.cvr_pct}%</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </details>
            )}
        </Card>
    );
}

function NewExperimentDialog({ open, onOpenChange, onCreated }) {
    const { t } = useTranslation();
    const [name, setName] = useState('');
    const [key, setKey] = useState('');
    const [metric, setMetric] = useState('conversion');
    const [langScope, setLangScope] = useState('');
    const [variants, setVariants] = useState([
        { key: 'control', label: '', weight: 50 },
        { key: 'variant_a', label: '', weight: 50 },
    ]);
    const [submitting, setSubmitting] = useState(false);

    const totalWeight = variants.reduce((s, v) => s + Number(v.weight || 0), 0);
    const validWeights = totalWeight === 100;

    const addVariant = () => {
        const idx = variants.length;
        setVariants([...variants, { key: `variant_${String.fromCharCode(97 + idx - 1)}`, label: '', weight: 0 }]);
    };
    const removeVariant = (idx) => setVariants(variants.filter((_, i) => i !== idx));
    const updateVariant = (idx, field, value) => {
        const copy = [...variants];
        copy[idx] = { ...copy[idx], [field]: field === 'weight' ? Number(value) : value };
        setVariants(copy);
    };

    const reset = () => {
        setName(''); setKey(''); setMetric('conversion'); setLangScope('');
        setVariants([{ key: 'control', label: '', weight: 50 }, { key: 'variant_a', label: '', weight: 50 }]);
    };

    const submit = async () => {
        if (!name || !key || !validWeights) return;
        setSubmitting(true);
        try {
            await api.post('/ab/experiments', {
                name,
                key,
                metric,
                variants: variants.map(v => ({ ...v, label: v.label || v.key })),
                lang_scope: langScope.trim() ? langScope.split(',').map(s => s.trim().toLowerCase()).filter(Boolean) : null,
                status: 'active',
            });
            toast.success(t('ab.created'));
            reset();
            onCreated();
        } catch (e) {
            toast.error(e.response?.data?.detail || t('common.generic_error'));
        } finally { setSubmitting(false); }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="font-display text-2xl flex items-center gap-2">
                        <FlaskConical className="w-5 h-5 text-[hsl(var(--accent))]" /> {t('ab.new_experiment')}
                    </DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-2">
                    <div className="grid sm:grid-cols-2 gap-3">
                        <div>
                            <Label>{t('ab.form_name')}*</Label>
                            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Hero CTA test" data-testid="ab-form-name" />
                        </div>
                        <div>
                            <Label>{t('ab.form_key')}*</Label>
                            <Input
                                value={key}
                                onChange={(e) => setKey(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_'))}
                                placeholder="hero_cta_test"
                                data-testid="ab-form-key"
                            />
                        </div>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-3">
                        <div>
                            <Label>{t('ab.form_metric')}</Label>
                            <Select value={metric} onValueChange={setMetric}>
                                <SelectTrigger data-testid="ab-form-metric"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="signup">{t('ab.metric_signup')}</SelectItem>
                                    <SelectItem value="conversion">{t('ab.metric_conversion')}</SelectItem>
                                    <SelectItem value="click">{t('ab.metric_click')}</SelectItem>
                                    <SelectItem value="cta_click">{t('ab.metric_cta_click')}</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label>{t('ab.form_lang_scope')}</Label>
                            <Input value={langScope} onChange={(e) => setLangScope(e.target.value)} placeholder="en, fr, de, es" data-testid="ab-form-lang-scope" />
                        </div>
                    </div>

                    <div>
                        <div className="flex items-center justify-between mb-2">
                            <Label>{t('ab.form_variants')} ({totalWeight}%)</Label>
                            <Button size="sm" variant="outline" onClick={addVariant} data-testid="ab-form-add-variant" className="gap-1 h-8">
                                <Plus className="w-3 h-3" /> {t('ab.form_add_variant')}
                            </Button>
                        </div>
                        <div className="space-y-2">
                            {variants.map((v, i) => (
                                <div key={i} className="grid grid-cols-12 gap-2 items-center">
                                    <Input
                                        className="col-span-3 text-xs"
                                        placeholder={t('ab.form_variant_key')}
                                        value={v.key}
                                        onChange={(e) => updateVariant(i, 'key', e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_'))}
                                        data-testid={`ab-form-variant-key-${i}`}
                                    />
                                    <Input
                                        className="col-span-6"
                                        placeholder={t('ab.form_variant_label')}
                                        value={v.label}
                                        onChange={(e) => updateVariant(i, 'label', e.target.value)}
                                        data-testid={`ab-form-variant-label-${i}`}
                                    />
                                    <Input
                                        className="col-span-2 tabular-nums"
                                        type="number" min="0" max="100"
                                        value={v.weight}
                                        onChange={(e) => updateVariant(i, 'weight', e.target.value)}
                                        data-testid={`ab-form-variant-weight-${i}`}
                                    />
                                    <Button size="sm" variant="ghost" className="col-span-1 text-[hsl(var(--destructive))]" onClick={() => removeVariant(i)} disabled={variants.length <= 2}>
                                        <Trash2 className="w-4 h-4" />
                                    </Button>
                                </div>
                            ))}
                        </div>
                        {!validWeights && (
                            <p className="text-xs text-[hsl(var(--destructive))] mt-2">⚠ {t('ab.form_weight_warn')} ({totalWeight}%)</p>
                        )}
                    </div>

                    <Button
                        onClick={submit}
                        disabled={submitting || !name || !key || !validWeights}
                        className="w-full h-11 bg-[hsl(var(--accent))] text-[hsl(220_13%_10%)] font-bold"
                        data-testid="ab-form-submit"
                    >
                        {t('ab.create_btn')}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
